'use client'

import React, { useEffect } from 'react'

import { useMemberTags, useSegmentCode, useSession } from '@rewardops/auth'
import { useFavouritingAndWishlistingStore } from '@rewardops/ui'
import { ResponseItem } from '@rewardops/utils'

import { useApi } from '~/components/hooks/use-api'

export enum FavouriteWishlistResource {
  Favourite = 'FAVOURITE',
  Wishlist = 'WISHLIST',
}

const useFavouritingWishlisting = () => {
  const { api } = useApi()
  const { data: session } = useSession()
  const utils = api.useContext()

  const segmentCode = useSegmentCode()
  const memberTags = useMemberTags()

  const {
    savedItemIds,
    setSavedItemIds,
    addToSavedItemIds,
    removeFromSavedItemIds,
    heartIconsInProgress,
    addHeartIconInProgress,
    removeHeartIconFromProgress,
    reset,
  } = useFavouritingAndWishlistingStore((state) => state)

  // if already fetched, do not fetch again
  const { data: memberSavedItems, isLoading: loading } =
    api.favouritesWishlist.getMemberSavedItemsSummary.useQuery(undefined, {
      enabled: !!session,
      staleTime: 5000,
    })

  const addToFavouriteOrWishlistMutation =
    api.favouritesWishlist.addToFavouriteOrWishlist.useMutation({
      onSuccess: () => {
        utils.favouritesWishlist.invalidate()
        utils.memberSavedItems.invalidate()
      },
    })

  const removeFromFavouriteOrWishlistMutation =
    api.favouritesWishlist.removeFromFavouriteOrWishlist.useMutation({
      onSuccess: () => {
        utils.favouritesWishlist.invalidate()
        utils.memberSavedItems.invalidate()
      },
    })

  useEffect(() => {
    if (memberSavedItems && !loading) {
      setSavedItemIds(new Set(memberSavedItems))
    }
  }, [memberSavedItems])

  const getResourceName = (itemType: string) =>
    itemType?.startsWith('affiliate')
      ? FavouriteWishlistResource.Favourite
      : FavouriteWishlistResource.Wishlist

  // add resource
  const addToFavouriteOrWishlist = async ({ item }: { item: ResponseItem }) => {
    const supplierItem = item?.supplier_items?.[0]
    const supplierItemId = Number(supplierItem?.id)

    if (heartIconsInProgress?.has(supplierItemId)) {
      return
    }

    const resourceName = getResourceName(item?.type)

    addHeartIconInProgress(supplierItemId)

    await addToFavouriteOrWishlistMutation
      .mutateAsync({
        resourceName,
        params: {
          member_tags: memberTags,
          segment: segmentCode,
          item_order_token: String(supplierItem?.order_token),
        },
      })
      .catch((err) => removeHeartIconFromProgress(supplierItemId))
    addToSavedItemIds(supplierItemId)
    removeHeartIconFromProgress(supplierItemId)
  }

  // remove resource
  const removeFromFavouriteOrWishlist = async ({
    item,
  }: {
    item: ResponseItem
  }) => {
    const supplierItem = item?.supplier_items?.[0]
    const supplierItemId = Number(supplierItem?.id)

    if (heartIconsInProgress?.has(supplierItemId)) {
      return
    }

    const resourceName = getResourceName(item?.type)

    addHeartIconInProgress(supplierItemId)
    await removeFromFavouriteOrWishlistMutation
      .mutateAsync({
        resourceName,
        params: {
          item_order_token: String(supplierItem?.order_token),
        },
      })
      .catch((err) => removeHeartIconFromProgress(supplierItemId))
    removeFromSavedItemIds(supplierItemId)
    removeHeartIconFromProgress(supplierItemId)
  }

  return {
    savedItemIds,
    heartIconsInProgress,
    addToFavouriteOrWishlist,
    removeFromFavouriteOrWishlist,
    resetState: reset,
  }
}

export { useFavouritingWishlisting }
